// frontend/src/homepage/howFeed.js
//
// The drifting live feed in the pinned "how it works" section, as pure data,
// plus the two scroll-side rules the section is driven by (progress -> stage,
// and "is the pin active").
//
// WHY IT IS SPLIT OUT OF THE COMPONENT. The interesting part of this feed is
// not what it looks like, it is that nine cards recycle through a fixed column
// FOREVER without ever landing on top of each other. That is an invariant over
// numbers, and the moment it is written as `element.style.transform` inside a
// rAF callback it becomes untestable: jsdom has no layout, so every height is
// zero and every card sits at the same y. Here the heights are just numbers a
// caller supplies, so a test can run ten thousand frames with real heights and
// assert that no two cards ever share a y-range.
//
// EVERY FUNCTION MUTATES ITS ITEMS IN PLACE, deliberately. This runs once per
// frame over nine objects; allocating nine replacements per frame to look
// functional would be the one measurable cost in the file. The items are owned
// by the component and never handed to React, so nothing observes the mutation.
//
// An item is `{ y, h, scanned, hot, dim, isLead, post }` plus whatever the
// caller attaches (the component attaches the DOM element).

/** Vertical gap between two cards, px. */
export const FEED_GAP = 14;
/** Where the first card's top sits when the column is first laid out, px. */
export const FEED_START_Y = 64;
/** Drift speed. Multiplied by dt and FEED_SPEED_SCALE - the prototype's units. */
export const FEED_SPEED = 0.55;
export const FEED_SPEED_SCALE = 0.05;
/** How far above the column a card must go before it is recycled, px. */
export const RECYCLE_ABOVE = 10;
/** How far below the column a recycled card re-enters, px. */
export const RECYCLE_BELOW = 20;
/** The height assumed for a card that cannot be measured (jsdom, display:none). */
export const FALLBACK_CARD_HEIGHT = 90;

/** One full sweep of the scan beam, ms. */
export const BEAM_PERIOD_MS = 6000;
/** The beam's travel, as fractions of the column height. */
export const BEAM_TOP = 0.08;
export const BEAM_SPAN = 0.84;

/**
 * Score at or above which a scanned lead is marked HOT in the feed.
 *
 * Deliberately lower than the ticker's 92 and the hero's 90: this is the
 * section explaining what the analyzer DOES, and a beam that discards eight of
 * nine cards demonstrates a filter rather than a product.
 */
export const FEED_HOT_SCORE = 88;

/** The stage boundaries, as fractions of the pinned section's scroll. */
export const STAGE_2_AT = 0.3;
export const STAGE_3_AT = 0.62;

/** The pinned wrapper's height, in viewport heights - the prototype's 340vh. */
export const PIN_WRAP_VH = 3.4;

/**
 * The step rail fills slightly ahead of the scroll so it reaches the last step
 * before the section ends - the prototype's `min(1, p * 1.15)`.
 */
export const RAIL_LEAD = 1.15;

/** Below this width the section is a static stack, never pinned. */
export const HOW_WIDE_MIN_PX = 1000;

/** The stream's stage label, indexed by stage. Verbatim from the prototype. */
export const STAGE_LABELS = ["", "LIVE FEED", "AI ANALYSIS", "DELIVERED"];

/**
 * The order posts appear in the column: real opportunities interleaved with
 * noise, so the beam is visibly making a decision rather than lighting
 * everything it touches. Verbatim from the design prototype.
 *
 * @param {object[]} leads
 * @param {object[]} noise
 * @returns {{post: object, isLead: boolean}[]}
 */
export function buildFeedPool(leads, noise) {
  const order = [
    leads[1],
    noise[0],
    leads[2],
    noise[1],
    leads[0],
    noise[2],
    leads[3],
    noise[3],
    leads[4],
  ];
  return order.filter(Boolean).map((post) => ({
    post,
    isLead: Boolean(post.pro),
    y: 0,
    h: FALLBACK_CARD_HEIGHT,
    scanned: false,
    hot: false,
    dim: false,
  }));
}

/**
 * Stack the cards from the top of the column, using their measured heights.
 *
 * @param {{h: number, y: number}[]} items
 * @param {{startY?: number, gap?: number}} [options]
 * @returns {typeof items} the same array.
 */
export function layoutFeed(items, options = {}) {
  const { startY = FEED_START_Y, gap = FEED_GAP } = options;
  let y = startY;
  for (const item of items) {
    item.y = y;
    y += item.h + gap;
  }
  return items;
}

/**
 * Where the beam is, for a given point in its cycle.
 *
 * A raised cosine rather than a sawtooth: the beam eases at both ends of its
 * travel, which reads as a head sweeping a column rather than a bar being
 * dragged. One period is BEAM_PERIOD_MS.
 *
 * @param {number} beamT ms into the current cycle.
 * @param {number} height the column's height, px.
 * @returns {number} the beam's y, px from the column's top.
 */
export function beamOffset(beamT, height) {
  const phase = ((beamT % BEAM_PERIOD_MS) / BEAM_PERIOD_MS) * Math.PI * 2;
  const eased = 0.5 - 0.5 * Math.cos(phase);
  return height * (BEAM_TOP + BEAM_SPAN * eased);
}

/**
 * Drift every card up by one frame, recycling any that has left the top.
 *
 * THE RECYCLE RULE IS THE WHOLE FILE. A card that leaves the top does not go
 * back to a fixed y - it goes BELOW THE LOWEST CARD CURRENTLY IN THE COLUMN,
 * plus one gap. Anything else (a fixed re-entry point, or the column height)
 * lands on whatever happens to be down there whenever the cards' heights sum to
 * less than the column, and the two then drift down the page overlapping. The
 * `max(…, height + RECYCLE_BELOW)` half only keeps a card from re-entering
 * inside the visible column when the stack is short.
 *
 * @param {{y: number, h: number}[]} items
 * @param {number} dtMs frame delta, already capped by the coordinator.
 * @param {number} height the column's height, px.
 * @param {object} [options]
 * @param {number} [options.speed]
 * @param {(item: object, index: number) => number} [options.measure] re-read a
 *   recycled card's height (its content is fixed, but a resize changes it).
 * @param {(item: object, index: number) => void} [options.onRecycle]
 * @returns {typeof items} the same array.
 */
export function advanceFeed(items, dtMs, height, options = {}) {
  const { speed = FEED_SPEED, measure, onRecycle } = options;
  const step = speed * dtMs * FEED_SPEED_SCALE;

  items.forEach((item, index) => {
    item.y -= step;
    if (item.y + item.h >= -RECYCLE_ABOVE) return;

    // The lowest bottom edge among the OTHER cards - `item` itself is above the
    // column and must not be its own reference point.
    let lowest = 0;
    for (const other of items) {
      if (other === item) continue;
      lowest = Math.max(lowest, other.y + other.h);
    }
    if (measure) {
      const measured = measure(item, index);
      if (measured > 0) item.h = measured;
    }
    item.y = Math.max(lowest + FEED_GAP, height + RECYCLE_BELOW);
    item.scanned = false;
    item.hot = false;
    item.dim = false;
    if (onRecycle) onRecycle(item, index);
  });

  return items;
}

/**
 * Mark whatever the beam has just crossed.
 *
 * Below stage 2 the beam is not running, and anything previously marked is
 * cleared - scrolling back up has to undo the story, not leave it half told.
 *
 * @param {object[]} items
 * @param {number} beamY the beam's y within the column.
 * @param {number} stage 1 | 2 | 3.
 * @param {number} [hotScore]
 * @returns {{changed: boolean, hottest: object|null}} `hottest` is the lead that
 *   was just marked hot, if any - what the phone's push notification shows.
 */
export function scanFeed(items, beamY, stage, hotScore = FEED_HOT_SCORE) {
  let changed = false;
  let hottest = null;

  for (const item of items) {
    if (stage < 2) {
      if (item.scanned || item.hot || item.dim) {
        item.scanned = false;
        item.hot = false;
        item.dim = false;
        changed = true;
      }
      continue;
    }
    if (item.scanned) continue;
    if (beamY <= item.y || beamY >= item.y + item.h) continue;

    item.scanned = true;
    changed = true;
    if (item.isLead && item.post.score >= hotScore) {
      item.hot = true;
      hottest = item;
    } else {
      item.dim = true;
    }
  }

  return { changed, hottest };
}

/**
 * @param {number} progress 0..1 through the pinned section.
 * @returns {1|2|3}
 */
export function stageForProgress(progress) {
  if (progress < STAGE_2_AT) return 1;
  if (progress < STAGE_3_AT) return 2;
  return 3;
}

/**
 * Scroll progress through the pinned wrapper, and whether the pin is holding.
 *
 * `p` is `-top / (height - viewport)` clamped to 0..1: 0 while the wrapper's
 * top is still below the viewport's top, 1 once its bottom has reached the
 * viewport's bottom. `active` is the prototype's `r.top <= 2 && -r.top < total`
 * - the window in which the sticky pin is actually stuck, which is the only
 * time the smoke should be drawing.
 *
 * @param {number} top the wrapper's bounding-rect top, viewport px.
 * @param {number} height the wrapper's height, px.
 * @param {number} viewportHeight
 * @returns {{p: number, active: boolean}}
 */
export function pinProgress(top, height, viewportHeight) {
  const total = height - viewportHeight;
  if (!(total > 0)) return { p: top <= 0 ? 1 : 0, active: false };
  const p = Math.max(0, Math.min(1, -top / total));
  return { p, active: top <= 2 && -top < total };
}

/**
 * The feed's per-frame driver, framework-free.
 *
 * It owns the beam clock and the stage, and it is the ONE place the "beam
 * resets when stage 2 begins" rule lives: the prototype zeroes `beamT` inside
 * `setStage(2)` so the analysis always starts from the top of the column
 * rather than wherever the beam happened to be. Everything DOM-shaped
 * (transforms, classes) is the caller's job, done from the values `tick`
 * returns.
 *
 * @param {object[]} items from buildFeedPool.
 * @param {object} [options]
 * @param {(item: object, index: number) => number} [options.measure] read an
 *   item's current height, px. Falls back to the item's stored height.
 * @param {number} [options.speed]
 * @param {number} [options.hotScore]
 */
export function createFeedRunner(items, options = {}) {
  const { measure, speed = FEED_SPEED, hotScore = FEED_HOT_SCORE } = options;
  let stage = 1;
  let beamT = 0;
  let laid = false;

  const measureItem = (item, index) => {
    if (!measure) return item.h;
    const h = measure(item, index);
    return h > 0 ? h : item.h;
  };

  const relayout = () => {
    items.forEach((item, index) => {
      item.h = measureItem(item, index);
    });
    layoutFeed(items);
    laid = true;
  };

  return {
    /** @returns {number} the current stage. */
    getStage: () => stage,
    /** @returns {number} ms into the beam's current sweep. */
    getBeamT: () => beamT,
    /** @returns {boolean} whether the column has been laid out yet. */
    isLaidOut: () => laid,
    /** Lay the column out again from the top (a resize, or a first frame). */
    relayout,
    /**
     * Change stage. Entering stage 2 restarts the beam at the top.
     *
     * @param {number} next 1 | 2 | 3.
     * @returns {boolean} true if the beam was reset.
     */
    setStage(next) {
      if (next === stage) return false;
      stage = next;
      if (next === 2) {
        beamT = 0;
        return true;
      }
      return false;
    },
    /**
     * Advance one frame.
     *
     * @param {number} dtMs capped frame delta.
     * @param {number} height the column's height, px.
     * @returns {{scanY: number, changed: boolean, hottest: object|null, recycled: object[]}}
     */
    tick(dtMs, height) {
      if (!laid) relayout();
      beamT = (beamT + dtMs) % BEAM_PERIOD_MS;
      const scanY = beamOffset(beamT, height);
      const recycled = [];
      advanceFeed(items, dtMs, height, {
        speed,
        measure: measureItem,
        onRecycle: (item) => recycled.push(item),
      });
      const { changed, hottest } = scanFeed(items, scanY, stage, hotScore);
      return { scanY, changed: changed || recycled.length > 0, hottest, recycled };
    },
  };
}
